import { ActionContext } from "vuex/types";
import {
  getUploadUrl,
  getProcessFileDownload,
  getProcessFileMaterialization,
  uploadProcessFiles
} from "@/services/api/process/ApiFile";
import { SnackQueue as Queue } from "@/plugins/SnackQueue";
import { uploadWithURL } from "@/utils/fileUtility";
import { App, General } from "@/utils/constants";
import {
  IObjectFileToUpload,
  IFileDownloadRequest,
  IProcessFileComplete,
  IPreSignedURL,
  IUploadResponse,
  IUpdatedDataStatus,
  IUploadAttachment,
  IProcessFileMaterialization
} from "@/store/types/file-type";
import { ResponseGenericType, ResponseType } from "@/store/types/types";
import { showErrorMessage } from "@/utils/messageUtility";
import { XhrUploadEvent } from "@/utils/utils-types";
import { RootState } from "@/store/types/root";

const state = {
  processId: null as string | null,
  processFileId: null as string | null,
  dispatchId: null as string | null,
  isDispatchUpload: false,
  updatedDataStatus: {} as IUpdatedDataStatus,
  listUploadFiles: [] as IObjectFileToUpload[],
  updateEdowebFile: {} as IObjectFileToUpload,
  needUpdateAttachments: {} as IUploadAttachment
};

type State = typeof state;

const mutations = {
  setUpdatedDataStatus: (state: State, typeStatus: IUpdatedDataStatus) => {
    state.updatedDataStatus = { time: Date.now(), ...typeStatus };
  },

  setDispatchUpload: (state: State, dispatchId: string) => {
    state.dispatchId = dispatchId;
    state.isDispatchUpload = true;
  },

  setListUploadFiles: (state: State, files: IObjectFileToUpload) => {
    if (Array.isArray(files)) {
      files.forEach(file => state.listUploadFiles.push(file));
    }
  },

  clearListUploadFiles: (state: State) => {
    state.listUploadFiles = [];
  },

  checkDoneUploadFiles: (state: State) => {
    const hasPendingFile = state.listUploadFiles.some(
      file => file.status === "pending"
    );
    if (!hasPendingFile) {
      Queue.open({
        id: "upload_files",
        canClose: true
      });
    }
  },

  setUpdateEdowebFile: (state: State, data: IObjectFileToUpload) => {
    state.updateEdowebFile = data;
  },

  setNeedUpdateAttachments: (state: State, data: IUploadAttachment) => {
    state.needUpdateAttachments = data;
  }
};

const actions = {
  fileUpload: async (
    { dispatch }: ActionContext<State, RootState>,
    fileObject: IObjectFileToUpload
  ) => {
    const results: ResponseGenericType = await uploadProcessFiles(fileObject);

    if (results.kind === "ok") {
      dispatch("updateListAttachmentsTrigger", {
        file: results.data,
        fileObject
      });
    } else {
      fileObject.status = "error";
      fileObject.isErro = true;
      showErrorMessage(results.message);
    }
  },

  updateListAttachmentsTrigger: (
    { commit, dispatch }: ActionContext<State, RootState>,
    { file, fileObject, service }: IUploadAttachment
  ) => {
    if (service !== "broadcast_local") {
      dispatch("sendAllChannels", {
        action: "update_list_attachments",
        service: "broadcast_local",
        data: { file, fileObject, service: "broadcast_local" },
        notSendAllSlaves: true
      });
    }
    commit("setNeedUpdateAttachments", { file, fileObject });
  },

  updateListAttachmentsAction: (
    { commit }: ActionContext<State, RootState>,
    { file, fileObject }: IUploadAttachment
  ) => {
    let stateName = "";
    if (file.dispatch_id) {
      if (file.editable) stateName = "dispatchFilesEditables";
      else stateName = "dispatchFiles";
    } else if (file.document_join_id) stateName = "jointFiles";
    else if (fileObject.archive || fileObject.revert_no_effect)
      stateName = "processAttachmentFiles";
    else if (file.process_id) {
      if (file.editable) stateName = "processFilesEditables";
      else {
        stateName = "processFiles";
        if (!file.primary) commit("addProcessFileSize", file.filesize);
      }
    }
    commit("updateFilesAttachments", { file, stateName });
  },

  requestDataProcessFile: async (
    _: ActionContext<State, RootState>,
    {
      fileIds,
      processId = "",
      editable = false,
      isDoe = false,
      useContent = false,
      printer_version = undefined,
      giveKnowledgeId = undefined
    }: IFileDownloadRequest
  ) => {
    const results: ResponseType<IProcessFileComplete> = await getProcessFileDownload(
      {
        fileIds,
        processId,
        editable,
        isDoe,
        useContent,
        printer_version,
        giveKnowledgeId
      }
    );

    if (results.kind === "Not Found")
      results.message = General.PROCESS_NOT_FOUND;
    if (results.kind === "ok") return results.data;
    else if (results.message) showErrorMessage(results.message);
  },

  requestProcessFileMaterialization: async (
    _: ActionContext<State, RootState>,
    params: IProcessFileMaterialization
  ) => {
    const results: ResponseType<IProcessFileComplete> = await getProcessFileMaterialization(
      params
    );

    if (results.kind === "ok") return results.data;
    else if (results.message) showErrorMessage(results.message);
  },

  setCancelUploadFile: (
    { state }: ActionContext<State, RootState>,
    fileIndex: string | number
  ) => {
    if (fileIndex === -1) {
      state.listUploadFiles.forEach((file: IObjectFileToUpload) => {
        if (file.status === "pending") {
          file.status = "abort";
          file.abort?.();
        }
      });
    } else {
      const file: IObjectFileToUpload | undefined = state.listUploadFiles.find(
        file => file.file_index === fileIndex
      );
      if (file) {
        file.status = "abort";
        file.abort?.();
      }
    }
  },

  uploadTrigger: ({ dispatch, state }: ActionContext<State, RootState>) => {
    const hasUploadFile = state.listUploadFiles.some(
      (file: IObjectFileToUpload) =>
        file.status === "pending" &&
        file.percentage > 0 &&
        file.percentage < 100
    );

    if (!hasUploadFile) {
      const fileToUpload = state.listUploadFiles.find(
        file => file.status === "pending" && file.percentage === 0
      );
      fileToUpload && dispatch("uploadObjectFile", fileToUpload);
    }
  },

  nextFileUpload: async (
    { dispatch, commit }: ActionContext<State, RootState>,
    { request, file, path }: IUploadResponse
  ) => {
    commit("checkDoneUploadFiles");
    if (request.kind === "ok") {
      file.s3Data = {
        name: file.file.name,
        url_s3: `suite/${path}`
      };
      if (file.edoweb) {
        const resultEdoweb = await dispatch(
          "validateDocumentEdowebAction",
          file.s3Data
        );
        file.s3Data.url_s3 = resultEdoweb.path;
        commit("setUpdateEdowebFile", file);
      } else await dispatch("fileUpload", file);
      dispatch("uploadTrigger");
    } else {
      file.isErro = true;
      file.status = "error";
      dispatch("uploadTrigger");
      commit("checkDoneUploadFiles");
      showErrorMessage(request.message);
    }
  },

  uploadObjectFile: async (
    { dispatch }: ActionContext<State, RootState>,
    fileObject: IObjectFileToUpload
  ) => {
    const testExt = /\.(\w+)$/g.exec(fileObject.attachment_name);
    if (!testExt) {
      showErrorMessage(App.PartAddressScreen.MESSAGES.INVALID_FORMAT);
      return;
    }
    const results: ResponseType<IPreSignedURL> = await getUploadUrl(
      testExt[1].toLowerCase()
    );
    let uploadPath = "";
    if (results.kind === "ok" && results.data) {
      uploadPath = await uploadWithURL(
        results.data.pre_signed_url,
        fileObject.file,
        (callback: XhrUploadEvent) => {
          const percentage =
            (callback.event.loaded / callback.event.total) * 100;
          fileObject.percentage = percentage;
          fileObject.abort = function() {
            callback.xhr.abort();
          };
          if (fileObject.status === "abort") {
            callback.xhr.abort();
          }
          if (fileObject.percentage === 100) {
            fileObject.status === "pending" && (fileObject.status = "done");
          }
        },
        ({ type }: Event) => {
          if (type === "abort") {
            fileObject.percentage = -1;
            fileObject.status = "abort";
          } else {
            fileObject.isErro = true;
            fileObject.status = "error";
          }
        }
      );
    }

    dispatch("nextFileUpload", {
      request: results,
      file: fileObject,
      path: uploadPath
    });
  }
};

const getters = {
  getUpdatedDataStatus: (state: State) => state.updatedDataStatus,
  getListUploadFiles: (state: State) => state.listUploadFiles,
  getUpdateEdowebFile: (state: State) => state.updateEdowebFile,
  needUpdateAttachments: (state: State) => state.needUpdateAttachments
};

export default {
  state,
  mutations,
  actions,
  getters
};
